import React, { createContext, useContext, useState, useEffect } from 'react';
import { getToken, getUser, setToken, setUser, removeToken, removeUser, getRefreshToken, setRefreshToken, removeRefreshToken } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to decode JWT and check expiration
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const decoded = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token is expired (with 30 second buffer)
    return decoded.exp && decoded.exp > currentTime + 30;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize auth state from localStorage
    const storedToken = getToken();
    const userData = getUser();
    
    console.log('AuthContext initialization:', { hasToken: !!storedToken, hasUser: !!userData });
    
    if (storedToken && userData) {
      // Validate token expiration
      if (isTokenValid(storedToken)) {
        console.log('Token is valid, setting auth state');
        setTokenState(storedToken);
        setUserState(userData);
      } else {
        // Token expired, clear it
        console.log('Token expired during initialization, clearing...');
        removeToken();
        removeUser();
      }
    } else {
      console.log('No token or user found in localStorage');
    }
    
    setLoading(false);
  }, []);

  const login = (token, userData, refreshToken) => {
    setToken(token);
    setUser(userData);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    setTokenState(token);
    setUserState(userData);
  };

  const logout = () => {
    removeToken();
    removeUser();
    removeRefreshToken();
    setTokenState(null);
    setUserState(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    setUserState(userData);
  };

  const value = {
    user,
    token,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
